import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  Calendar,
  TrendingDown,
  TrendingUp,
  Activity,
  ExternalLink,
  Quote,
  Flame,
} from "lucide-react";
import AnatomyViewer from "../components/anatomy/AnatomyViewer";

/**
 * Iter Y — Public client-facing progress portal.
 *
 * Renders the sanitized payload from GET /api/portal/p/{token}. No auth.
 * Designed to be:
 *   • Mobile-first (most opens are SMS-driven on a phone)
 *   • Client-friendly (no SOAP, no billing, no AI confidence numbers)
 *   • Sharable enough that a spouse / provider / attorney can read it
 *
 * The body chart heatmap (with sage/amber/terracotta trend coloring) is the
 * hero visual, as approved.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BACKEND}/api/portal/p/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 410) setError({ kind: "expired", message: detail || "This link has expired." });
        else if (status === 404) setError({ kind: "missing", message: detail || "Report not found." });
        else setError({ kind: "error", message: detail || "Could not load this report." });
      })
      .finally(() => setLoading(false));
  }, [token]);

  const heatmap = data?.history?.heatmap || {};
  const session = data?.session;
  const branding = data?.branding;

  const painImprovement = useMemo(() => {
    const out = session?.outcome;
    if (!out) return null;
    const pb = Number(out.pain_before), pa = Number(out.pain_after);
    if (!Number.isFinite(pb) || !Number.isFinite(pa) || pb <= 0) return null;
    return {
      delta: pb - pa,
      pct: Math.round(((pb - pa) / pb) * 100),
      before: pb,
      after: pa,
    };
  }, [session]);

  const romGains = useMemo(() => {
    return (session?.outcome?.rom_measurements || [])
      .map((r) => {
        const b = Number(r.before), a = Number(r.after);
        if (!Number.isFinite(b) || !Number.isFinite(a) || b <= 0) return null;
        const gain = Math.round(((a - b) / b) * 100);
        return { label: r.label, before: b, after: a, gain };
      })
      .filter(Boolean);
  }, [session]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-50">
        <div className="inline-block w-12 h-12 rounded-full border-4 border-sage-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-50 p-6">
        <div className="bg-white border border-paper-200 rounded-3xl p-10 max-w-md text-center" data-testid={`portal-error-${error.kind}`}>
          <h1 className="font-heading text-2xl text-ink-900 mb-2">
            {error.kind === "expired" ? "This link has expired" : "Report unavailable"}
          </h1>
          <p className="text-ink-700 text-sm">
            {error.message} If you still need this report, ask your therapist for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-paper-50" data-testid="client-portal-page">
      {/* Therapist branding header */}
      <header className="bg-white border-b border-paper-200">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-5 flex items-center gap-4">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="logo" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-heading text-lg shrink-0"
              style={{ backgroundColor: branding?.brand_color || "#7E8E63" }}
            >
              {((branding?.studio_name || branding?.therapist_name || "P")[0] || "P").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-medium text-ink-900 truncate">{branding?.studio_name || branding?.therapist_name}</div>
            {branding?.tagline && <div className="text-xs text-ink-500 truncate">{branding.tagline}</div>}
          </div>
          {branding?.booking_link && (
            <a
              href={branding.booking_link}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto min-h-[40px] px-4 rounded-full text-sm font-medium bg-sage-500 hover:bg-sage-600 text-white inline-flex items-center gap-2"
              data-testid="portal-booking-cta"
            >
              Book again <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8 space-y-6">
        {/* Greeting */}
        <div data-testid="portal-greeting">
          <div className="label-overline text-sage-600 mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Session recap
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">
            Hi {data.first_name},
          </h1>
          <p className="text-ink-700 mt-2 max-w-xl">
            Here&rsquo;s a recap of your session on{" "}
            <span className="font-medium text-ink-900">
              {session?.date ? new Date(session.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "today"}
            </span>
            .
          </p>
        </div>

        {/* Therapist message */}
        {data.therapist_message && (
          <div className="bg-sage-500/5 border border-sage-500/20 rounded-3xl p-5 sm:p-6" data-testid="portal-therapist-message">
            <Quote className="w-5 h-5 text-sage-600 mb-2" />
            <p className="text-ink-900 leading-relaxed whitespace-pre-wrap">{data.therapist_message}</p>
            <div className="text-xs text-ink-500 mt-3">— {branding?.therapist_name}</div>
          </div>
        )}

        {/* Headline KPIs */}
        <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="portal-kpis">
          {painImprovement && (
            <KpiTile
              label="Pain"
              value={`${painImprovement.before} → ${painImprovement.after}`}
              sub={painImprovement.delta > 0 ? `↓ ${painImprovement.pct}% improvement` : painImprovement.delta < 0 ? `↑ ${Math.abs(painImprovement.pct)}%` : "no change"}
              icon={painImprovement.delta > 0 ? TrendingDown : TrendingUp}
              accent={painImprovement.delta > 0 ? "sage" : painImprovement.delta < 0 ? "terracotta" : "ink"}
              testid="portal-kpi-pain"
            />
          )}
          {romGains.length > 0 && (
            <KpiTile
              label="Range of motion gains"
              value={`${romGains.filter((g) => g.gain > 0).length} of ${romGains.length}`}
              sub="movements improved"
              icon={Activity}
              accent="sage"
              testid="portal-kpi-rom"
            />
          )}
          <KpiTile
            label="Visits to date"
            value={data.history?.totals?.completed || 0}
            sub={`${data.history?.totals?.improved || 0} with pain reduction`}
            icon={Calendar}
            accent="ink"
            testid="portal-kpi-visits"
          />
        </section>

        {/* Hero body chart */}
        <section className="bg-white border border-paper-200 rounded-3xl p-4 sm:p-6" data-testid="portal-body-chart">
          <div className="mb-3">
            <div className="label-overline text-terracotta-600 mb-1 flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5" /> Your treatment map
            </div>
            <h2 className="font-heading text-xl text-ink-900">Areas we&rsquo;ve worked on</h2>
            <p className="text-xs text-ink-500 mt-1">
              Sage = improving over time · amber = holding steady · terracotta = needs more attention. Larger dots = more sessions on that area.
            </p>
          </div>
          <AnatomyViewer
            heatmap={heatmap}
            sex="m"
            testIdPrefix="portal-anatomy"
          />
        </section>

        {/* Worked areas this visit */}
        {(session?.worked_areas || []).length > 0 && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5" data-testid="portal-worked-areas">
            <div className="label-overline text-ink-500 mb-2">Today, we focused on</div>
            <div className="flex flex-wrap gap-1.5">
              {session.worked_areas.map((m) => (
                <span key={m.id} className="text-sm px-3 py-1 rounded-full bg-paper-100 text-ink-900" data-testid={`portal-worked-${m.id}`}>
                  {m.label}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Pain & ROM detail */}
        {(romGains.length > 0 || painImprovement) && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="portal-progress-detail">
            <div className="label-overline text-ink-500 mb-3">Your progress today</div>
            {painImprovement && (
              <div className="mb-4">
                <div className="text-sm text-ink-700">
                  Pain dropped from <strong>{painImprovement.before}/10</strong> to <strong>{painImprovement.after}/10</strong>{" "}
                  ({painImprovement.delta > 0 ? "↓" : "↑"} {painImprovement.pct}%).
                </div>
              </div>
            )}
            {romGains.length > 0 && (
              <ul className="space-y-2">
                {romGains.map((g) => (
                  <li key={g.label} className="text-sm flex items-center justify-between gap-3" data-testid={`portal-rom-${g.label}`}>
                    <span className="text-ink-900">{g.label}</span>
                    <span className={`font-medium ${g.gain > 0 ? "text-sage-700" : g.gain < 0 ? "text-terracotta-700" : "text-amber-700"}`}>
                      {g.before}° → {g.after}° ({g.gain > 0 ? "+" : ""}{g.gain}%)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Home care + recommendations (client-friendly) */}
        {session?.outcome?.client_text_message && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="portal-homecare">
            <div className="label-overline text-terracotta-600 mb-2">Home care &amp; what&rsquo;s next</div>
            <p className="text-ink-900 leading-relaxed whitespace-pre-wrap">{session.outcome.client_text_message}</p>
          </section>
        )}

        {/* Photos (only when therapist opted in) */}
        {data.photos && (data.photos.front || data.photos.side || data.photos.back) && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="portal-photos">
            <div className="label-overline text-ink-500 mb-3">Posture snapshots from this session</div>
            <div className="grid grid-cols-3 gap-3">
              {["front", "side", "back"].map((k) =>
                data.photos[k] ? (
                  <div key={k} className="aspect-[2/3] bg-paper-50 rounded-2xl overflow-hidden border border-paper-200">
                    <img
                      src={`${BACKEND}${data.photos[k]}`}
                      alt={`${k} posture`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div key={k} />
                )
              )}
            </div>
          </section>
        )}

        {/* Visit timeline */}
        {(data.history?.sessions || []).length > 1 && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="portal-timeline">
            <div className="label-overline text-ink-500 mb-3">Your visit timeline</div>
            <ol className="space-y-2">
              {data.history.sessions.map((s) => (
                <li
                  key={s.session_num}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 ${s.is_current ? "bg-sage-500/10 border border-sage-500/30" : "bg-paper-50 border border-paper-100"}`}
                  data-testid={`portal-timeline-row-${s.session_num}`}
                >
                  <div className="text-sm text-ink-900 inline-flex items-center gap-2">
                    <span className="font-medium">Visit {s.session_num}</span>
                    {s.date && <span className="text-xs text-ink-500">{new Date(s.date).toLocaleDateString()}</span>}
                    {s.is_current && <span className="text-[10px] uppercase tracking-[0.14em] text-sage-700 font-bold">today</span>}
                  </div>
                  {s.pain_before != null && s.pain_after != null && (
                    <div className="text-xs text-ink-700">
                      Pain {s.pain_before} → {s.pain_after}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-ink-500 py-8 space-y-1">
          <div>
            Prepared by <span className="text-ink-700">{branding?.therapist_name}</span>
            {branding?.studio_name && <> · {branding.studio_name}</>}
          </div>
          <div>
            This link is private and expires {data?.meta?.expires_at ? new Date(data.meta.expires_at).toLocaleDateString() : "soon"}.
          </div>
          <div className="mt-2">
            <span className="text-ink-400">Posturra · Clinical progress portal</span>
          </div>
        </footer>
      </main>
    </div>
  );
}

function KpiTile({ label, value, sub, icon: Icon, accent = "ink", testid }) {
  const accentText =
    accent === "sage" ? "text-sage-700" :
    accent === "terracotta" ? "text-terracotta-700" :
    accent === "amber" ? "text-amber-700" :
    "text-ink-900";
  const accentBg =
    accent === "sage" ? "bg-sage-500/10" :
    accent === "terracotta" ? "bg-terracotta-500/10" :
    accent === "amber" ? "bg-amber-500/10" :
    "bg-paper-100";
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-overline text-ink-500">{label}</span>
        <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${accentBg}`}>
          {Icon && <Icon className={`w-4 h-4 ${accentText}`} />}
        </span>
      </div>
      <div className={`font-heading text-2xl ${accentText}`}>{value}</div>
      {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
    </div>
  );
}
